// src/components/inputs/IntervalInput.js
import React, { useEffect, useRef, useState } from "react";
import { InlineMath } from "react-katex";
import { parseRootFractionInput, formatLatexPreview as formatNumberLatex } from "./RootFractionInput";
import SetKeyboard from "./SetKeyboard";

/* =================== Helpers =================== */

const TOL = 1e-9;

const isInf = (s) => /^(?:\+?inf(?:inity)?|\+?∞)$/i.test(s);
const isNinf = (s) => /^(?:-inf(?:inity)?|-∞)$/i.test(s);
const UNION_SPLITTER = /(\s*∪\s*|\s*\bU\b\s*|\s*\bunion\b\s*|\s*\bsuma\b\s*)/i;

const COMPLEMENT_RE = /^(?:ℝ|R)\s*([\-\/\\]?)\s*\{(.+)\}$/i;
const isEmptySet = (s) => /^(?:∅|Ø|\\?varnothing|\\?emptyset)$/i.test(String(s).trim());
const isAllReals = (s) => /^(?:ℝ|R)$/i.test(String(s).trim());

const eq = (a, b) => Math.abs(a - b) <= TOL;

function parseNumberLoose(txtRaw) {
  const s = String(txtRaw || "").trim();
  // mixed number: a b/c
  let m = s.match(/^([+-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, A, B, C] = m;
    const val = Number(A) + Number(B) / Number(C || 1);
    return (sign === "-" ? -1 : 1) * val;
  }
  // fraction: a/b
  m = s.match(/^([+-]?)\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, N, D] = m;
    if (Number(D) === 0) return null;
    return (sign === "-" ? -1 : 1) * (Number(N) / Number(D));
  }
  // decimal with , or .
  m = s.match(/^([+-]?)\s*(\d+(?:[.,]\d+)?)$/);
  if (m) {
    const [, sign, dec] = m;
    const v = Number(dec.replace(",", "."));
    if (!Number.isFinite(v)) return null;
    return (sign === "-" ? -1 : 1) * v;
  }
  return null;
}

function parseEndpoint(raw) {
  if (raw == null) return null;
  const txt = String(raw).trim();
  if (isInf(txt)) return +Infinity;
  if (isNinf(txt)) return -Infinity;
  // pozwól na pierwiastki/łamki z RootFractionInput
  const v1 = parseRootFractionInput(txt.replace(",", "."));
  if (Number.isFinite(v1)) return v1;
  const v2 = parseNumberLoose(txt);
  return Number.isFinite(v2) ? v2 : null;
}

/* ===== Ambiguity & splitting ===== */

function hasDecimalComma(s) {
  return /\d,\d/.test(String(s || ""));
}

/**
 * splitInsideToSides:
 * - Preferuje ';' jako separator.
 * - Jeśli nie ma ';' i jest DOKŁADNIE JEDEN przecinek, a po obu stronach nie ma przecinka dziesiętnego,
 *   to uznaje ten przecinek za separator (np. "[8,9]").
 * - W pozostałych przypadkach z przecinkami zgłasza __ambiguous (wymuś średnik).
 * Zwraca: [left, right] lub { __ambiguous: true } lub null.
 */
function splitInsideToSides(inside) {
  const text = String(inside ?? "").trim();
  if (!text) return null;

  // 1) średnik wygrywa zawsze
  const semiIdx = text.indexOf(";");
  if (semiIdx !== -1) {
    const L = text.slice(0, semiIdx).trim();
    const R = text.slice(semiIdx + 1).trim();
    return L && R ? [L, R] : null;
  }

  // 2) pojedynczy przecinek może być separatorem, jeśli nie jest dziesiętny
  const commas = (text.match(/,/g) || []).length;
  if (commas === 1) {
    const [L, R] = text.split(",").map((t) => t.trim());
    if (hasDecimalComma(L) || hasDecimalComma(R)) return { __ambiguous: true };
    return L && R ? [L, R] : null;
  }

  // 3) wiele przecinków → niejednoznaczne
  if (commas > 1) return { __ambiguous: true };

  // 4) brak separatora
  return null;
}

/** Rozcina listę punktów w dopełnieniu ℝ\{…} bez psucia "1,5" */
function splitPointList(text) {
  const s = String(text ?? "").trim();
  if (!s) return [];
  const semiParts = s.split(";").map((t) => t.trim()).filter(Boolean);
  if (semiParts.length > 1) return semiParts;
  if (hasDecimalComma(s)) return [s];
  return s.split(",").map((t) => t.trim()).filter(Boolean);
}

export function parseFiniteNumberList(inside,withRaw=false) {
 const text=String(inside??"").trim().replace(/\u00a0/g," ");
 if(!text)return {kind:"finite",points:[]};
 let pieces;
 if(text.includes(";"))pieces=text.split(";");
 else if(/,\s+/.test(text))pieces=text.split(/,\s+/);
 else if(/^[+-]?\d+(?:[.,]\d+)?(?:\s+[+-]?\d+(?:[.,]\d+)?)+$/.test(text))pieces=text.split(/\s+/);
 else if(text.includes(",")){
  // A comma before a sign/fraction is a clear delimiter. Digit-comma-digit
  // without an explicit delimiter could instead be a decimal number.
  if(/^[+-]?\d+,\d+$/.test(text))return {kind:"finite",points:[parseEndpoint(text)],...(withRaw?{pieces:[text]}:{})};
  if(!text.includes(".")&&/\d,\d/.test(text))return {kind:"ambiguous"};
  pieces=text.split(",");
 }else pieces=[text];
 if(pieces.some(piece=>!piece.trim()))return null;
 const points=[];
 for(const piece of pieces){
  const value=parseEndpoint(piece.trim());if(!Number.isFinite(value))return null;
  if(!points.some(p=>Math.abs(p-value)<=TOL))points.push(value);
 }
 return {kind:"finite",points:points.sort((a,b)=>a-b),...(withRaw?{pieces}: {})};
}

function parseFinitePointSet(inside) {
  const text = String(inside ?? "").trim();
  if (!text) return [];
  const pieces = splitPointList(text);
  const points = [];
  for (const piece of pieces) {
    const v = parseEndpoint(piece);
    if (!Number.isFinite(v)) return null;
    if (!points.some((u) => Math.abs(u - v) <= TOL)) points.push(v);
  }
  return points.sort((a, b) => a - b);
}

/* ===== parser jednego przedziału (pełny zapis) ===== */
function parseOneInterval(str) {
  const s = String(str).trim();
  // Accept also <...> as a closed interval shorthand.
  if (!s || !/^[\(\[<]/.test(s) || !/[\)\]>]$/.test(s)) return null;

  const lB = s[0];
  const rB = s[s.length - 1];
  const inside = s.slice(1, -1);

  const split = splitInsideToSides(inside);
  if (!split) return null;
  if (split.__ambiguous) return { __ambiguous: true };

  const [lRaw, rRaw] = split;
  const left = parseEndpoint(lRaw);
  const right = parseEndpoint(rRaw);
  if (left == null || right == null) return null;

  // walidacja: lewy ≤ prawy (pojedynczy punkt tylko z [a;a])
  const leftClosed = lB === "[" || lB === "<";
  const rightClosed = rB === "]" || rB === ">";

  if (!(left < right || (left === right && leftClosed && rightClosed))) {
    // Keep dedicated marker so caller can show better hint.
    return { __invalidOrder: true, left, right, leftClosed, rightClosed };
  }

  return { left, right, leftClosed, rightClosed };
}

function normalizeIntervals(list) {
  return (list || [])
    .slice()
    .sort((A, B) => {
      const a = A.left, b = B.left;
      if (a === b) return A.right === B.right ? 0 : A.right < B.right ? -1 : 1;
      if (a === -Infinity) return -1;
      if (b === -Infinity) return 1;
      if (a === Infinity) return 1;
      if (b === Infinity) return -1;
      return a - b;
    });
}

/* ====== KANONIZACJA: scalanie sumy przedziałów ======
   - scala nachodzące
   - scala stykające się, jeśli punkt styku należy do co najmniej jednego (brak "dziury")
*/
function canonicalizeIntervals(intervals) {
  const sorted = normalizeIntervals(intervals);
  if (!sorted.length) return [];

  const merged = [];
  let cur = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const nxt = sorted[i];

    // jeśli następny zaczyna się po zakończeniu bieżącego -> rozłączne
    if (nxt.left > cur.right + TOL) {
      merged.push(cur);
      cur = { ...nxt };
      continue;
    }

    // jeśli jest "dziura" w punkcie styku (dokładnie styczne i oba otwarte)
    if (eq(nxt.left, cur.right) && !cur.rightClosed && !nxt.leftClosed) {
      merged.push(cur);
      cur = { ...nxt };
      continue;
    }

    // w pozostałych przypadkach: nachodzą lub stykają się bez dziury -> scalamy
    // nowy right to max(right)
    if (nxt.right > cur.right + TOL) {
      cur.right = nxt.right;
      cur.rightClosed = !!nxt.rightClosed;
    } else if (eq(nxt.right, cur.right)) {
      // ten sam prawy kraniec -> domknięcie jeśli którykolwiek domknięty
      cur.rightClosed = !!cur.rightClosed || !!nxt.rightClosed;
    }
    // lewy kraniec i jego domknięcie pozostają od cur (bo cur ma mniejszy left)
  }

  merged.push(cur);
  return merged;
}

/* ====== raw/latex z obiektów (do komunikatu "można uprościć") ====== */

function numToRaw(v) {
  if (v === Infinity) return "∞";
  if (v === -Infinity) return "-∞";
  if (!Number.isFinite(v)) return String(v);
  // preferuj zapis z przecinkiem dziesiętnym
  const s = String(v);
  return s.includes(".") ? s.replace(".", ",") : s;
}

function intervalObjToRaw(I) {
  const l = I.leftClosed ? "[" : "(";
  const r = I.rightClosed ? "]" : ")";
  return `${l}${numToRaw(I.left)}; ${numToRaw(I.right)}${r}`;
}

function intervalsUnionToRaw(intervals) {
  if (!intervals || !intervals.length) return "";
  return intervals.map(intervalObjToRaw).join(" ∪ ");
}

function endpointLatex(text) {
  if (!text) return "";
  const t = String(text).trim();
  if (isInf(t)) return "\\infty";
  if (isNinf(t)) return "-\\infty";
  const latex = (formatNumberLatex(t, "") || "").trim();
  return latex || t.replace(".", "{,}");
}

function endpointValueToLatex(v) {
  if (v === Infinity) return "\\infty";
  if (v === -Infinity) return "-\\infty";
  const raw = numToRaw(v);
  return endpointLatex(raw);
}

function intervalObjToLatex(I) {
  const l = I.leftClosed ? "[" : "(";
  const r = I.rightClosed ? "]" : ")";
  const L = endpointValueToLatex(I.left);
  const R = endpointValueToLatex(I.right);
  return `${l}${L};\\,${R}${r}`;
}

function intervalsUnionToLatex(intervals) {
  if (!intervals || !intervals.length) return "";
  return intervals.map(intervalObjToLatex).join("\\;\\cup\\;");
}

/** eksportowalny parser — używamy w checkerach */
export function parseIntervalInput(raw) {
  const s0 = String(raw ?? "").trim();
  if (!s0) return null;

  if (isAllReals(s0)) return { kind: "all" };
  if (isEmptySet(s0)) return { kind: "empty" };

  const finiteSet = s0.match(/^\{(.*)\}$/);
  if (finiteSet && !UNION_SPLITTER.test(s0)) {
    return parseFiniteNumberList(finiteSet[1]);
  }

  // dopełnienie ℝ\{...}
  const comp = s0.match(COMPLEMENT_RE);
  if (comp) {
    const inside = comp[2];
    const pieces = splitPointList(inside);
    if (pieces.length <= 1) {
      const p = parseEndpoint(inside);
      return Number.isFinite(p) ? { kind: "complement", points: [p], point: p } : null;
    }
    const points = [];
    for (const piece of pieces) {
      const v = parseEndpoint(piece);
      if (!Number.isFinite(v)) return null;
      if (!points.some((u) => Math.abs(u - v) <= TOL)) points.push(v);
    }
    points.sort((a, b) => a - b);
    return { kind: "complement", points, point: points[0] };
  }

  // sumy przedziałów
  const parts = s0.split(UNION_SPLITTER).filter((t) => t && !UNION_SPLITTER.test(t));
  const intervalsRaw = [];
  for (const part of parts) {
    if (isEmptySet(part)) continue;
    const pointSet = part.trim().match(/^\{(.*)\}$/);
    if (pointSet) {
      const points = parseFinitePointSet(pointSet[1]);
      if (!points) return null;
      intervalsRaw.push(...points.map(point => ({ left: point, right: point, leftClosed: true, rightClosed: true })));
      continue;
    }
    const one = parseOneInterval(part);
    if (!one) return null;
    if (one.__ambiguous) return { kind: "ambiguous" };
    if (one.__invalidOrder) {
      return {
        kind: "invalid_order",
        interval: {
          left: one.left,
          right: one.right,
          leftClosed: !!one.leftClosed,
          rightClosed: !!one.rightClosed,
        },
      };
    }
    intervalsRaw.push(one);
  }
  if (!intervalsRaw.length) return null;

  const intervalsNormalized = normalizeIntervals(intervalsRaw);
  const intervalsCanonical = canonicalizeIntervals(intervalsNormalized);

  const needsSimplification = intervalsCanonical.length !== intervalsNormalized.length;

  return {
    kind: "intervals",
    // ✅ klucz: intervals jest KANONICZNE (z tym będą porównywane odpowiedzi)
    intervals: intervalsCanonical,
    // dodatkowe info dla komunikatu po sprawdzeniu
    originalIntervals: intervalsNormalized,
    needsSimplification,
    simplifiedRaw: needsSimplification ? intervalsUnionToRaw(intervalsCanonical) : null,
    simplifiedLatex: needsSimplification ? intervalsUnionToLatex(intervalsCanonical) : null,
  };
}

/* ===== latex helpers (z surowego inputu) ===== */

function intervalLatex(rawIntervalText) {
  const s = String(rawIntervalText).trim();
  if (!/^[\(\[<]/.test(s)) return null;

  const isClosed = /[\)\]>]$/.test(s);
  const lB = s[0];
  const rB = isClosed ? s[s.length - 1] : "";
  const inside = s.slice(1, isClosed ? -1 : s.length);

  const split = splitInsideToSides(inside);
  if (!split || split.__ambiguous) return null;

  const [lRaw, rRaw] = split;
  const L = endpointLatex(lRaw);
  const R = endpointLatex(rRaw);
  const leftBracket = lB === "<" ? "[" : lB;
  const rightBracket = rB === ">" ? "]" : rB;
  return `${leftBracket}${L};\\,${R}${rightBracket}`;
}

/** PUBLIC: latex z inputu; withX → poprzedź „x \in” */
export function intervalToLatex(raw, { withX = false } = {}) {
  if (!raw) return "";
  const local = String(raw).trim();

  // ℝ
  if (isAllReals(local)) {
    const setLatex = "\\mathbb{R}";
    return withX ? `x\\in\\;${setLatex}` : setLatex;
  }

  // ∅
  if (isEmptySet(local)) {
    const setLatex = "\\varnothing";
    return withX ? `x\\in\\;${setLatex}` : setLatex;
  }

  // ℝ\{…}
  const finiteSet = local.match(/^\{(.*)\}$/);
  if(finiteSet && !UNION_SPLITTER.test(local)){
    const parsed=parseFiniteNumberList(finiteSet[1],true);
    if(!parsed||parsed.kind!=="finite")return "";
    const latex="\\{"+(parsed.pieces||[]).map(endpointLatex).join(";\\,")+"\\}";
    return withX ? "x\\in\\;"+latex : latex;
  }

  const comp = local.match(COMPLEMENT_RE);
  let setLatex = "";
  if (comp) {
    const operator = (comp[1] || "").trim(); // "-", "/", "\" (opcjonalnie)
    const inside = comp[2];
    const pieces = splitPointList(inside);
    const itemsLatex = pieces.length > 0
      ? pieces.map((p) => endpointLatex(p)).join(",\\,")
      : endpointLatex(inside);
    const opLatex = operator === "-" ? "-" : "\\setminus";
    setLatex = `\\mathbb{R}\\,${opLatex}\\,\\{${itemsLatex}\\}`;
  } else {
    // suma przedziałów (preview pokazuje jak user wpisał)
    const parts = local
      .split(UNION_SPLITTER)
      .filter((t) => t && !UNION_SPLITTER.test(t))
      .map((p) => (isEmptySet(p) ? "\\varnothing" : /^\{.*\}$/.test(p.trim()) ? intervalToLatex(p) : (intervalLatex(p) || p)));
    setLatex = parts.join("\\;\\cup\\;");
  }

  return withX ? `x\\in\\;${setLatex}` : setLatex;
}

/* =================== IntervalInput =================== */

export default function IntervalInput({
  value,
  onChange,
  disabled,
  label = "D",
  placeholder = "np. (-∞, 1 1/2) ∪ (3/2, ∞)  lub  (1,5; 3,5)  lub  ℝ\\{-2; 3/2}",
  showPreview = true,
  asString = false,
  previewWithX = false,
  previewVariable = "",
  prefixAbove = false,
  showRootFractionTools = false,
}) {
  const valueToRaw = (v) => {
    if (typeof v === "string") return v;
    if (v == null) return "";
    if (typeof v === "object") {
      if (typeof v.raw === "string") return v.raw;
      if (v.raw && typeof v.raw === "object" && typeof v.raw.raw === "string") {
        return v.raw.raw;
      }
      for (const key of ["value", "text", "input", "expr", "latex"]) {
        if (typeof v[key] === "string") return v[key];
      }
      return "";
    }
    return String(v);
  };

  const [local, setLocal] = useState(valueToRaw(value));
  const [showBrackets, setShowBrackets] = useState(false);
  const [kbdOpen, setKbdOpen] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const next = valueToRaw(value);
    setLocal((prev) => (prev === next ? prev : next));
  }, [value]);

  useEffect(() => {
    const parsed = parseIntervalInput(local);

    // ✅ tutaj już wraca parsed.intervals jako KANONICZNE
    // oraz parsed.needsSimplification / simplifiedRaw / simplifiedLatex do komunikatu po sprawdzeniu
    onChange && onChange(asString ? local : { raw: local, parsed });

    if (parsed && parsed.kind === "ambiguous") {
      setErr("Nie można jednoznacznie odczytać przecinków. Oddziel liczby średnikiem (;), np. {2,5; 3,5} albo (1,5; 3,5).");
    } else if (!parsed && /^\{/.test(local.trim())) {
      setErr("Niepoprawny format zbioru. Oddziel liczby średnikami, np. {2,5; 3,5}.");
    } else {
      setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, asString]);

  const setCaret = (pos) => {
    const el = inputRef.current; if (!el) return;
    el.focus(); el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current; if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    setLocal(next);
    setTimeout(() => setCaret(s + text.length), 0);
  };

  const handleKbdAction = (type, payload) => {
    const el = inputRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    if (type === "insert") return insertAtCursor(String(payload ?? ""));
    if (type === "backspace") {
      if (s !== e) { const next = local.slice(0, s) + local.slice(e); setLocal(next); setTimeout(() => setCaret(s), 0); }
      else if (s > 0) { const next = local.slice(0, s - 1) + local.slice(e); setLocal(next); setTimeout(() => setCaret(s - 1), 0); }
      return;
    }
    if (type === "delete") {
      if (s !== e) { const next = local.slice(0, s) + local.slice(e); setLocal(next); setTimeout(() => setCaret(s), 0); }
      else if (e < local.length) { const next = local.slice(0, s) + local.slice(e + 1); setLocal(next); setTimeout(() => setCaret(s), 0); }
      return;
    }
    if (type === "moveLeft")  { setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0); return; }
    if (type === "moveRight") { setTimeout(() => setCaret(Math.min(local.length, Math.max(s, e) + 1)), 0); return; }
    if (type === "clear")     { setLocal(""); setTimeout(() => setCaret(0), 0); return; }
    if (type === "enter")     { return; }
    if (type === "toggle")    { setKbdOpen((v) => !v); return; }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
      {prefixAbove && (<div style={{ marginBottom: 2, lineHeight: 1 }}><InlineMath math={'x\\in'} /></div>)}

      {/* Rząd 1: toggle klawiatury (po LEWEJ), input + szybkie klawisze */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={handleKbdAction}
          compact
        />

        <input
          ref={inputRef}
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className="input-secondary"
          aria-label={label}
          style={{
            flex: "1 1 260px",
            minWidth: 0,
            width: "100%",
            maxWidth: 420,
          }}
        />

        <button type="button" onClick={() => insertAtCursor(" ∪ ")} disabled={disabled} className="btn-secondary" title="Wstaw sumę (∪)">U</button>
        <button type="button" onClick={() => insertAtCursor("ℝ")}   disabled={disabled} className="btn-secondary" title="Wstaw ℝ">R</button>
        <button type="button" onClick={() => insertAtCursor("∅")}   disabled={disabled} className="btn-secondary" title="Wstaw ∅ (zbiór pusty)">∅</button>
        <button type="button" onClick={() => insertAtCursor("∞")}   disabled={disabled} className="btn-secondary" title="Wstaw ∞ (dla −∞ dopisz minus)">∞</button>
        {showRootFractionTools && <button type="button" onClick={() => insertAtCursor("√")} disabled={disabled} className="btn-secondary" title="Wstaw pierwiastek" aria-label="Wstaw pierwiastek">√</button>}
        {showRootFractionTools && <button type="button" onClick={() => insertAtCursor("/")} disabled={disabled} className="btn-secondary" title="Wstaw kreskę ułamkową" aria-label="Wstaw kreskę ułamkową">/</button>}
        {showRootFractionTools && <button type="button" onClick={() => insertAtCursor("; ")} disabled={disabled} className="btn-secondary" title="Oddziel końce przedziału" aria-label="Oddziel końce przedziału">;</button>}
        <button type="button" onClick={() => setShowBrackets((v) => !v)} disabled={disabled} className="btn-secondary" title={showBrackets ? "Ukryj przyciski nawiasów" : "Pokaż przyciski nawiasów"}>{"{…}"}</button>
      </div>

      {/* Rząd 2: panel klawiatury */}
      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={handleKbdAction}
            compact
            panelIndent={36}
          />
        </div>
      )}

      {/* Rząd 3: nawiasy – DOMYŚLNIE UKRYTE */}
      {showBrackets && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
          <button type="button" onClick={() => insertAtCursor("(")} disabled={disabled} className="btn-secondary" title="Otwórz nawias (">(</button>
          <button type="button" onClick={() => insertAtCursor(")")} disabled={disabled} className="btn-secondary" title="Zamknij nawias )">)</button>
          <button type="button" onClick={() => insertAtCursor("[")} disabled={disabled} className="btn-secondary" title="Otwórz nawias kwadratowy [">[</button>
          <button type="button" onClick={() => insertAtCursor("]")} disabled={disabled} className="btn-secondary" title="Zamknij nawias kwadratowy ]">]</button>
          <button type="button" onClick={() => insertAtCursor("{")} disabled={disabled} className="btn-secondary" title="Otwórz nawias klamrowy {">{"{"}</button>
          <button type="button" onClick={() => insertAtCursor("}")} disabled={disabled} className="btn-secondary" title="Zamknij nawias klamrowy }">{"}"}</button>
        </div>
      )}

      {/* Podgląd + błąd */}
      {showPreview && local && (
        <div style={{ marginTop: 6 }}>
          <div style={{ pointerEvents: "none" }}>
            <strong>Podgląd:</strong>{" "}
            <InlineMath
              math={`\\displaystyle ${
                previewVariable
                  ? `${previewVariable}\\in\\;${intervalToLatex(local)}`
                  : intervalToLatex(local, { withX: previewWithX })
              }`}
            />
          </div>

          {/^\{[+-]?\d+,\d+\}$/.test(local.trim()) && <p style={{fontSize:13}}>Odczytano jedną liczbę dziesiętną. Jeśli chodziło o dwie liczby, rozdziel je średnikiem lub przecinkiem ze spacją, np. {"{2;5}"} albo {"{2, 5}"}.</p>}
      {err && (
            <div style={{ color: "var(--danger, #dc3545)", marginTop: 4, fontSize: 13 }}>
              {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
